import { defineComponent, h } from 'vue'

import { bsComponentProps, useBsComponent } from '../useBsComponent'
import { useVm } from '../../../composables/useVm'
import { domHandlers } from '../quasarHandlers'

/**
 * The label/hint/content wrapper around a field. Every section is a slot, and each is left out entirely
 * when its slot is absent rather than rendering an empty container.
 *
 * NOTE, carried over: the error section is gated on the WARNING slot, so an error slot only renders when
 * a warning slot is also present (BUG, pinned).
 */
export default defineComponent({
  name: 'BsFormField',

  /**
   * Declared so `input` is an emitted event rather than a fallthrough attribute: undeclared, the
   * `onInput` the render attaches stays in `$attrs` and Vue applies it to the root element too, so a
   * single DOM input event ran the handler twice.
   */
  emits: ['input'],

  props: {
    ...bsComponentProps,
    wrap: { type: Boolean, default: false },
    dense: { type: Boolean, default: false },
    multiLineHint: { type: Boolean, default: false }
  },

  setup (props, { emit }) {
    return { ...useBsComponent({ vm: useVm(), props, emit }) }
  },

  methods: {
    // Renamed from `__renderX`: Vue does not proxy keys beginning with `_` onto the instance.
    renderTitleSection () {
      if (!this.$slots.title && !this.$slots.hint) {
        return void 0
      }

      return h('div', {
        class: 'bs-form-field__container'
      }, [
        this.$slots.title && h('div', {
          class: 'bs-form-field__title',
          'aria-hidden': true
        }, this.slot(this, 'title')),
        this.$slots.hint && h('p', {
          // 'bs-form-field__hint' was the staticClass beside this one.
          class: ['bs-form-field__hint', this.multiLineHint ? 'bs-form-field__hint--multiline' : void 0]
        }, this.slot(this, 'hint'))
      ])
    },

    renderWarningSection () {
      if (!this.$slots.warning) {
        return void 0
      }

      return h('div', {
        class: 'bs-form-field__warning'
      }, this.slot(this, 'warning'))
    },

    renderErrorSection () {
      if (!this.$slots.warning) {
        return void 0
      }

      return h('div', {
        class: 'bs-form-field__error'
      }, this.slot(this, 'error'))
    }
  },

  render () {
    return h('div', {
      class: ['bs-form-field', {
        'bs-form-field--wrap': this.wrap,
        'bs-form-field--dense': this.dense
      }],
      ...domHandlers(this.getListeners())
    }, [
      this.renderTitleSection(),
      this.renderWarningSection(),
      this.renderErrorSection(),
      h('div', {
        class: 'bs-form-field__content'
      }, this.slot(this, 'default'))
    ])
  }
})
